import { useContext, useMemo, useState } from 'react';
import {
  AgxBodyText,
  AgxButton,
  AgxColumn,
  AgxHeader,
  AgxRow,
  cleanFullAddress,
  formatCurrency,
  Images,
  AgxBadge,
  NswContractStage,
  QldContractStage,
  PropertyAddress,
} from '@urbanx/agx-ui-components';
import { Link, useParams } from 'react-router-dom';
import { MenuTitles, Pages } from 'constants/menu';
import { CampaignTabHeader } from 'components';
import { ContentLayout, PageLayout } from 'layout';
import { useContractsList } from 'hooks';
import './Contracts.scss';
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client';
import {
  ContractActionRequestContractAction,
  usePerformContractAction,
  useStaffGetCampaign,
} from '@urbanx/agx.campaigns.client';
import { useDialog } from '@urbanx/agx.ui-components2';
import DestructiveActionConfirmationModal from 'components/destructiveActionConfirmationModal/DestructiveActionConfirmationModal';
import { useGetAgencyDetails } from '@urbanx/agx.agencies.client';
import ContractOptions from './components/ContractOptions';
import { getContractStageBadgeType } from './contractStageBadge';
import { filterContractsForCampaignState } from './contractStageFilters';
import ContractWithdrawOfferModal from './components/contract-withdraw-offer-modal/ContractWithdrawOfferModal';

const Contracts = () => {
  const { userAccount } = useContext(AuthContext);
  const { agencyId, campaignId } = useParams();
  const { data: agency } = useGetAgencyDetails(agencyId || '', !!agencyId);
  const { data: campaign } = useStaffGetCampaign(campaignId || '', !!campaignId);
  const { contracts, isLoading, invalidateContracts } = useContractsList();
  const { performContractAction } = usePerformContractAction({
    onSuccess: async () => {
      await invalidateContracts();
    },
    onError: () => {},
  });
  const [withdrawContractId, setWithdrawContractId] = useState<string>();

  const terminationDialog = useDialog(DestructiveActionConfirmationModal, {
    title: 'Terminate Contract?',
    message: 'Terminating this contract cannot be reversed.',
    confirmButtonText: 'Terminate Contract',
  });

  const onPerformContractAction = (
    contractId: string,
    contractAction: ContractActionRequestContractAction
  ) => {
    if (!campaignId) return;

    const performAction = () =>
      performContractAction({
        campaignId,
        contractId,
        contractAction,
      });

    if (contractAction === ContractActionRequestContractAction.Terminated) {
      terminationDialog.open({ onConfirm: performAction });
      return;
    }

    if (contractAction === ContractActionRequestContractAction.Cancelled) {
      setWithdrawContractId(contractId);
      return;
    }

    performAction();
  };

  const filteredContracts = useMemo(() => {
    if (!contracts) return [];

    return filterContractsForCampaignState(contracts, campaign?.state);
  }, [campaign?.state, contracts]);

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as unknown as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CONTRACTS}
        isCampaigns={true}
      >
        <AgxColumn extraClasses="campaignContracts" veryLargeGap fill>
          <CampaignTabHeader
            title="All Contracts"
            titleId="allContractsTitle"
            actions={<AgxButton hollow medium disabled text="New Contract" />}
          />
          <table className="campaignContractTable">
            <tbody>
              <tr>
                <th align="left" className="column-name">
                  <AgxBodyText small>Contract</AgxBodyText>
                </th>
                <th align="left" className="column-name">
                  <AgxBodyText small>Sale Price</AgxBodyText>
                </th>
                <th align="left" className="column-name">
                  <AgxBodyText small>Initial Deposit</AgxBodyText>
                </th>
                <th align="left" className="column-name">
                  <AgxBodyText small>Balance Deposit</AgxBodyText>
                </th>
                <th align="left" className="column-name">
                  <AgxBodyText small>Status</AgxBodyText>
                </th>
                <th align="right" className="column-name action-column"></th>
              </tr>
              {!!filteredContracts &&
                filteredContracts.length > 0 &&
                filteredContracts.map((contract) => (
                  <tr key={contract.contractId ?? contract.contractNames}>
                    <td>
                      <AgxRow mediumGap centered>
                        <Images.SparkleSmall />
                        {contract.contractId ? (
                          <Link
                            to={`${contract.contractId}/${userAccount.roles?.includes(StaffRole.WorkflowContributor) ? Pages.CONTRACT_TASKS : Pages.BUYERS_INFORMATION}`}
                            className="campaignContractLink"
                          >
                            <AgxHeader size={4}>{contract.contractNames}</AgxHeader>
                          </Link>
                        ) : (
                          <AgxHeader size={4}>{contract.contractNames}</AgxHeader>
                        )}
                      </AgxRow>
                    </td>
                    <td>
                      <AgxColumn fill smallGap>
                        {contract.salePrice ? formatCurrency(contract.salePrice, 0) : '$--'}
                      </AgxColumn>
                    </td>
                    <td>
                      <AgxColumn fill smallGap>
                        {contract.initialDeposit ? formatCurrency(contract.initialDeposit) : '$--'}
                      </AgxColumn>
                    </td>
                    <td>
                      <AgxColumn fill smallGap>
                        {contract.balanceDeposit ? formatCurrency(contract.balanceDeposit) : '$--'}
                      </AgxColumn>
                    </td>
                    <td>
                      <AgxBadge
                        text={contract.stage ?? ''}
                        type={getContractStageBadgeType(
                          contract.stage as QldContractStage | NswContractStage
                        )}
                      />
                    </td>
                    <td className="contractOptionsCell">
                      {contract.contractId && (
                        <ContractOptions
                          contractId={contract.contractId}
                          campaignState={campaign?.state}
                          stage={contract.stage as QldContractStage | NswContractStage}
                          onPerformContractAction={onPerformContractAction}
                          dataTestId={`contract-options-${contract.contractId}`}
                        />
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {(!filteredContracts || filteredContracts.length === 0) && (
            <AgxColumn fill centered centerJustified extraClasses="contractDetailNoContracts">
              <AgxBodyText>
                {isLoading ? 'Loading...' : 'No Contracts have been submitted for this campaign'}
              </AgxBodyText>
            </AgxColumn>
          )}
        </AgxColumn>
      </ContentLayout>
      {withdrawContractId && (
        <ContractWithdrawOfferModal
          contractId={withdrawContractId}
          isOpen
          setIsOpen={(isOpen) => !isOpen && setWithdrawContractId(undefined)}
        />
      )}
    </PageLayout>
  );
};

export default Contracts;
